import { BLOCKS } from '@contentful/rich-text-types'
import { ComponentCtaButtonColor, ComponentCtaButtonType, TypeSectionSingleColumn } from '../../types'
import { ComponentContentTypes } from '../../types/content-types'
import { ComponentDynamicKey } from '../../types/component-dynamic'
import { createEntry } from '../../utils/contentful-entry'
import type { PatchPageStructure } from '../types'
import { BackgroundColor } from '../../types/background'
import { CtaGroupLayout } from '../../types/item-cta-group'
import { TitleSize } from '../../types/typography'
import { TypePage } from '../../types/page'
import { getConfig } from '../../../config'

const config = getConfig()

const editorUrl = config.urls.aiEditorNewDoc

const editorCtaLinkEntry = createEntry(ComponentContentTypes.ComponentLink, 'EditorCtaLink', {
  targetUrl: editorUrl,
  targetTab: 'New ( _blank )',
  internalName: editorUrl,
  appendCurrentQueryParams: true,
})

const editorCtaButtonEntry = createEntry(ComponentContentTypes.ComponentCtaButton, 'EditorCta', {
  ctaText: {
    data: {},
    content: [
      {
        data: {},
        content: [
          {
            data: {},
            marks: [
              {
                type: 'bold',
              },
            ],
            value: 'Start writing',
            nodeType: 'text',
          },
        ],
        nodeType: BLOCKS.PARAGRAPH,
      },
    ],
    nodeType: BLOCKS.DOCUMENT,
  },
  internalName: 'EditorCta',
  color: ComponentCtaButtonColor.WhiteTextGreenButton,
  type: ComponentCtaButtonType.Link,
  targetLink: editorCtaLinkEntry,
})

const createEditorCtaSection = (): TypeSectionSingleColumn => {
  const featureHighlightItem = createEntry(ComponentContentTypes.ItemFeatureHighlight, 'EditorCtaFeatureHighlight', {
    internalName: 'EditorCtaFeatureHighlight',
    title: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [
                {
                  type: 'bold',
                },
              ],
              value: 'Access More Features in Grammarly Editor',
              nodeType: 'text',
            },
          ],
          nodeType: BLOCKS.PARAGRAPH,
        },
      ],
      nodeType: BLOCKS.DOCUMENT,
    },
    description: {
      data: {},
      content: [
        {
          data: {},
          content: [
            {
              data: {},
              marks: [],
              value:
                'Check grammar, rewrite sentences, and more—all for free—when you write documents in Grammarly Editor.',
              nodeType: 'text',
            },
          ],
          nodeType: BLOCKS.PARAGRAPH,
        },
      ],
      nodeType: BLOCKS.DOCUMENT,
    },
    titleSize: TitleSize.HL,
  })

  const ctaGroupItem = createEntry(ComponentContentTypes.ItemCtaGroup, 'EditorCtaGroup', {
    internalName: 'EditorCtaGroup',
    layout: CtaGroupLayout.Row,
    ctAs: [editorCtaButtonEntry],
  })

  return createEntry(ComponentContentTypes.SectionSingleColumn, 'EditorCtaSection', {
    internalName: 'EditorCtaSection',
    content: [featureHighlightItem, ctaGroupItem],
    background: {
      color: BackgroundColor.N0_24,
    },
  }) as TypeSectionSingleColumn
}

const createPersistentClientCtaSection = (isLtvExperimentCtaGroup: boolean): TypeSectionSingleColumn => {
  if (isLtvExperimentCtaGroup) {
    const dynamicComponent = createEntry(ComponentContentTypes.ComponentDynamic, 'PersistentClientCtaSection', {
      internalName: 'PersistentClientCtaSection',
      key: ComponentDynamicKey.PersistentClientCtaSection,
    })

    return createEntry(ComponentContentTypes.SectionSingleColumn, 'PersistentClientCtaSection', {
      internalName: 'PersistentClientCtaSection',
      content: [dynamicComponent],
      background: {
        color: BackgroundColor.N0_24,
      },
      isRemovedVerticalPadding: false,
    }) as TypeSectionSingleColumn
  }

  const persistentClientFeatureHighlight = createEntry(
    ComponentContentTypes.ItemFeatureHighlight,
    'PersistentClientFeatureHighlight',
    {
      internalName: 'PersistentClientFeatureHighlight',
      title: {
        data: {},
        content: [
          {
            data: {},
            content: [
              {
                data: {},
                marks: [],
                value: 'Start Writing With Grammarly',
                nodeType: 'text',
              },
            ],
            nodeType: BLOCKS.PARAGRAPH,
          },
        ],
        nodeType: BLOCKS.DOCUMENT,
      },
      description: {
        data: {},
        content: [
          {
            data: {},
            content: [
              {
                data: {},
                marks: [],
                value:
                  "You've signed up for Grammarly. The next step is to get Grammarly up and running on your desktop or web browser, so you can get real-time feedback as you write.",
                nodeType: 'text',
              },
            ],
            nodeType: BLOCKS.PARAGRAPH,
          },
        ],
        nodeType: BLOCKS.DOCUMENT,
      },
      titleSize: TitleSize.HXL,
    }
  )

  const persistentClientCtaGroup = createEntry(ComponentContentTypes.ItemCtaGroup, 'PersistentClientCtaSection', {
    internalName: 'PersistentClientCtaSection',
    layout: CtaGroupLayout.Row,
    ctAs: [
      createEntry(ComponentContentTypes.ComponentCtaButton, 'DownloadDesktopAppCta', {
        ctaText: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [
                    {
                      type: 'bold',
                    },
                  ],
                  value: 'Download desktop app',
                  nodeType: 'text',
                },
              ],
              nodeType: BLOCKS.PARAGRAPH,
            },
          ],
          nodeType: BLOCKS.DOCUMENT,
        },
        internalName: 'DownloadDesktopAppCta',
        color: ComponentCtaButtonColor.WhiteTextGreenButton,
        type: ComponentCtaButtonType.LlamaInstall,
      }),
      createEntry(ComponentContentTypes.ComponentCtaButton, 'InstallBrowserExtensionCta', {
        ctaText: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [
                    {
                      type: 'bold',
                    },
                  ],
                  value: 'Install browser extension',
                  nodeType: 'text',
                },
              ],
              nodeType: BLOCKS.PARAGRAPH,
            },
          ],
          nodeType: BLOCKS.DOCUMENT,
        },
        internalName: 'InstallBrowserExtensionCta',
        color: ComponentCtaButtonColor.Secondary,
        type: ComponentCtaButtonType.ExtensionInstall,
      }),
    ],
  })

  return createEntry(ComponentContentTypes.SectionSingleColumn, 'PersistentClientCtaSection', {
    internalName: 'PersistentClientCtaSection',
    content: [persistentClientFeatureHighlight, persistentClientCtaGroup],
    background: {
      color: BackgroundColor.N0_24,
    },
    isRemovedVerticalPadding: false,
  }) as TypeSectionSingleColumn
}

const createHeroReplacement = (ctaSection: TypeSectionSingleColumn, pageStructure: TypePage) => {
  const existingHero = pageStructure.fields?.heroSection
  const existingSections = pageStructure.fields?.sections || []

  const newSections = existingHero ? [existingHero, ...existingSections].filter(Boolean) : existingSections

  return {
    ...pageStructure,
    fields: {
      ...pageStructure.fields,
      heroSection: ctaSection,
      sections: newSections,
    },
  }
}

const patchPageStructure: PatchPageStructure = (
  pageStructure,
  experimentService,
  _userService,
  browserService,
  _facadeService,
  _pageSlug
) => {
  const ctaVariant = experimentService.getSignedInDesktopEditorPersistentClientCtaAssignment()
  const toolsLtvNavVariant = experimentService.getToolsLtvNavAssignment()
  const isMobileOrTablet = browserService.isMobileOrTablet

  if (ctaVariant === 'test_editor_cta') {
    const editorCtaSection = createEditorCtaSection()
    return createHeroReplacement(editorCtaSection, pageStructure)
  }

  if (toolsLtvNavVariant === 'test' && !isMobileOrTablet) {
    const personalizedCtaSection = createPersistentClientCtaSection(true)
    return createHeroReplacement(personalizedCtaSection, pageStructure)
  }

  if (ctaVariant === 'test_persistent_client_cta') {
    const persistentClientCtaSection = createPersistentClientCtaSection(false)
    return createHeroReplacement(persistentClientCtaSection, pageStructure)
  }

  return pageStructure
}

export const SignedInDesktopEditorPersistentClientCtaExperiment = {
  patchPageStructure,
}
